/**
 * RecoveryPhraseSection — fraza de recuperare în pagina Cont.
 *
 * Fraza se generează automat la înregistrare și se rotește la schimbarea
 * parolei master. De aici userul poate, mereu după ce confirmă parola master
 * într-un pop-up:
 *   - s-o descarce ca „kit de recuperare" (.txt)
 *   - s-o vadă pe ecran
 *   - s-o înlocuiască cu una nouă (cea veche încetează să funcționeze)
 *
 * Serverul întoarce fraza sigilată cu cheia publică a userului; browserul o
 * deschide cu cheia privată din sesiune. Conturile create înainte de această
 * funcționalitate nu au frază și o generează tot de aici.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { ApiError } from '@/api/client';
import Modal from '@/components/Modal';
import RecoveryPhraseCard from '@/components/RecoveryPhraseCard';
import {
  buildRecoveryBundle,
  generateRecoveryPhrase,
  openSealedRecoveryPhrase,
} from '@/crypto/recovery';
import { base64ToBytes } from '@/crypto/sodium';
import { reauthHash } from '@/lib/reauth';
import { downloadRecoveryKit } from '@/lib/recoveryKit';
import { useAuthStore } from '@/stores/auth';

type Intent = 'download' | 'reveal' | 'generate';
type Dialog =
  | { kind: 'password'; intent: Intent }
  | { kind: 'show'; phrase: string; fresh: boolean };

export default function RecoveryPhraseSection() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const keyPair = useAuthStore((s) => s.keyPair);
  const email = useAuthStore((s) => s.user?.email ?? '');
  const status = useQuery({
    queryKey: ['recovery-status'],
    queryFn: () => authApi.recoveryStatus(),
  });

  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const enabled = status.data?.enabled ?? false;

  const close = () => {
    setDialog(null);
    setPassword('');
    setError(null);
  };

  const open = (intent: Intent) => {
    setNotice(null);
    setError(null);
    setPassword('');
    setDialog({ kind: 'password', intent });
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (dialog?.kind !== 'password' || !keyPair) return;
    const { intent } = dialog;
    setError(null);
    setBusy(true);
    try {
      const authHash = await reauthHash(password);
      if (!authHash) {
        setError(t('recovery.wrong_password'));
        return;
      }
      setPassword('');

      if (intent === 'generate') {
        const phrase = generateRecoveryPhrase();
        await authApi.recoverySetup({
          auth_hash: authHash,
          ...(await buildRecoveryBundle(phrase, keyPair)),
        });
        await queryClient.invalidateQueries({ queryKey: ['recovery-status'] });
        setDialog({ kind: 'show', phrase, fresh: true });
        return;
      }

      const { recovery_phrase_sealed } = await authApi.recoveryPhrase(authHash);
      const phrase = await openSealedRecoveryPhrase(
        base64ToBytes(recovery_phrase_sealed),
        keyPair,
      );
      if (intent === 'download') {
        downloadRecoveryKit(phrase, email, t);
        setNotice(t('recovery.downloaded_notice'));
        close();
      } else {
        setDialog({ kind: 'show', phrase, fresh: false });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 404 ? t('recovery.not_stored') : err.detail);
      } else if (err instanceof Error) setError(err.message);
      else setError(t('recovery.generic_error'));
    } finally {
      setBusy(false);
    }
  };

  const title =
    dialog?.kind === 'show'
      ? t('recovery.title')
      : dialog?.intent === 'download'
        ? t('recovery.modal_download_title')
        : dialog?.intent === 'reveal'
          ? t('recovery.modal_reveal_title')
          : t('recovery.modal_generate_title');

  return (
    <div data-testid="recovery-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium">{t('recovery.title')}</div>
          <div className="text-xs text-[var(--text-dim)]">
            {enabled
              ? t('recovery.status_enabled', {
                  when: status.data?.set_at
                    ? new Date(status.data.set_at).toLocaleDateString(i18n.resolvedLanguage)
                    : '—',
                })
              : t('recovery.status_disabled')}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {enabled && (
            <>
              <button
                type="button"
                className="btn-primary text-xs"
                data-testid="recovery-download-kit"
                onClick={() => open('download')}
              >
                {t('recovery.download')}
              </button>
              <button
                type="button"
                className="btn-outline text-xs"
                data-testid="recovery-reveal"
                onClick={() => open('reveal')}
              >
                {t('recovery.reveal')}
              </button>
            </>
          )}
          <button
            type="button"
            className="btn-outline text-xs"
            data-testid="recovery-generate"
            onClick={() => open('generate')}
          >
            {enabled ? t('recovery.replace') : t('recovery.generate')}
          </button>
        </div>
      </div>

      {!enabled && status.data && (
        <p className="text-xs mt-2" style={{ color: 'var(--c-amber, #d97706)' }}>
          {t('recovery.warning_not_set')}
        </p>
      )}
      {notice && (
        <p
          className="text-xs mt-2 text-green-600 dark:text-green-400"
          data-testid="recovery-notice"
        >
          {notice}
        </p>
      )}

      <Modal open={dialog !== null} onClose={close} title={title} maxWidth="max-w-lg">
        {dialog?.kind === 'password' && (
          <form onSubmit={submitPassword} className="space-y-3" data-testid="recovery-modal">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {dialog.intent === 'download'
                ? t('recovery.download_hint')
                : dialog.intent === 'reveal'
                  ? t('recovery.reveal_hint')
                  : enabled
                    ? t('recovery.replace_hint')
                    : t('recovery.setup_hint')}
            </p>
            <input
              type="password"
              autoComplete="current-password"
              className="input font-mono"
              placeholder={t('auth.master_password')}
              aria-label={t('auth.master_password')}
              data-testid="recovery-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoFocus
            />
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-outline" onClick={close} disabled={busy}>
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={busy || !password}
                data-testid="recovery-submit"
              >
                {busy ? t('recovery.working') : t('recovery.continue')}
              </button>
            </div>
          </form>
        )}

        {dialog?.kind === 'show' && (
          <div className="space-y-3">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {dialog.fresh ? t('recovery.show_hint_new') : t('recovery.show_hint')}
            </p>
            <RecoveryPhraseCard phrase={dialog.phrase} email={email} />
            <div className="flex justify-end">
              <button
                type="button"
                className="btn-primary"
                data-testid="recovery-hide"
                onClick={close}
              >
                {t('recovery.hide')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
